import { HeatmapLight, SettingLineLight, SlantPilotCustom } from '@repo/go-icons';
import { ActionsBar, Checkbox, Switch, Tooltip } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { DoubleTooltipSidebarItem } from '../../../shared/components/DoubleTooltipSidebarItem/DoubleTooltipSidebarItem';
import { SelectableWellboreList } from '../../../shared/components/SelectableWellboreList/SelectableWellboreList';
import type { HeatmapWidgetState } from '../../store/HeatmapWidgetState';
import { CHART_VISIBILITY_OPTIONS } from '../SettingsDrawer/SettingsOptions';

import styles from './heatmap-action-bar.module.scss';

type HeatmapActionBarProps = {
  widgetState: HeatmapWidgetState;
};

export const HeatmapActionBar = observer(({ widgetState }: HeatmapActionBarProps) => {
  const { settings, wellbores } = widgetState;
  const { t } = useTranslation('heatmapWidget');

  return (
    <ActionsBar>
      <ActionsBar.TopBar>
        <DoubleTooltipSidebarItem
          outerTitle={t('Wellbore-selection')}
          icon={<SlantPilotCustom />}
          innerTitle={() =>
            wellbores.list.length ? (
              <SelectableWellboreList
                wellbores={wellbores.list}
                activeTitle={t('Active')}
                value={wellbores.selected?.id ?? null}
                onChange={(id) => widgetState.setWellboreById(id)}
              />
            ) : (
              <div style={{ padding: 8 }}>{t('Heatmap-no-wellbores')}</div>
            )
          }
        />
        <Tooltip
          placement="left"
          trigger={['click']}
          title={
            <div className={styles.options}>
              {CHART_VISIBILITY_OPTIONS.map(({ key, label }) => {
                const isChecked = settings.isChartVisible(key);
                const isDisabled = settings.visibleChartsCount <= 1 && isChecked;
                return (
                  <div key={key} className={styles.option}>
                    <Checkbox
                      checked={isChecked}
                      onChange={(checked?: boolean) =>
                        settings.setChartVisibility(key, typeof checked === 'boolean' ? checked : !isChecked)
                      }
                      disabled={isDisabled}
                    />
                    <span className={styles.label}>{t(label)}</span>
                  </div>
                );
              })}
            </div>
          }
        >
          <ActionsBar.Item>
            <HeatmapLight />
          </ActionsBar.Item>
        </Tooltip>
        <Tooltip placement="left" title={t('View-settings')}>
          <ActionsBar.Item onClick={() => widgetState.setSettingsDrawerOpen(true)}>
            <SettingLineLight />
          </ActionsBar.Item>
        </Tooltip>
      </ActionsBar.TopBar>
      <ActionsBar.BottomBar>
        <Switch
          style={{ marginLeft: 4 }}
          orientation="vertical"
          size="m"
          checked={settings.depthUnit === 'ft'}
          leftNode={t('M')}
          rightNode={t('Ft')}
          onChange={(value) => settings.setDepthUnit(value ? 'ft' : 'm')}
        />
      </ActionsBar.BottomBar>
    </ActionsBar>
  );
});
